import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  CALENDAR_TOOLBAR_VIEWPORT_PADDING_PX,
  computeCalendarToolbarPosition,
  type CalendarToolbarPosition,
} from "../../utils/calendarToolbarPosition";

interface CalendarActionToolbarProps {
  anchorRef: RefObject<HTMLElement | null>;
  ariaLabel: string;
  children: ReactNode;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
}

/**
 * A viewport-positioned action sidecar for calendar blocks. Rendering through
 * a portal lets it escape the day column's clipping and overlap layers.
 */
export function CalendarActionToolbar({
  anchorRef,
  ariaLabel,
  children,
  onMouseEnter,
  onMouseLeave,
}: CalendarActionToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<CalendarToolbarPosition | null>(
    null,
  );

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const toolbar = toolbarRef.current;
    if (!anchor || !toolbar) return;

    const anchorRect = anchor.getBoundingClientRect();
    const padding = CALENDAR_TOOLBAR_VIEWPORT_PADDING_PX;
    const outsideViewport =
      anchorRect.bottom <= padding ||
      anchorRect.top >= window.innerHeight - padding ||
      anchorRect.right <= padding ||
      anchorRect.left >= window.innerWidth - padding;

    if (outsideViewport) {
      setPosition(null);
      return;
    }

    const toolbarRect = toolbar.getBoundingClientRect();
    const nextPosition = computeCalendarToolbarPosition(
      anchorRect,
      { width: toolbarRect.width, height: toolbarRect.height },
      { width: window.innerWidth, height: window.innerHeight },
    );

    setPosition((current) =>
      current?.top === nextPosition.top &&
      current.left === nextPosition.left &&
      current.side === nextPosition.side
        ? current
        : nextPosition,
    );
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updatePosition);
    if (anchorRef.current) resizeObserver?.observe(anchorRef.current);
    if (toolbarRef.current) resizeObserver?.observe(toolbarRef.current);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      resizeObserver?.disconnect();
    };
  }, [anchorRef, updatePosition]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label={ariaLabel}
      className="fixed z-[60] inline-flex flex-col gap-0.5 rounded-lg border border-border bg-surface p-0.5 shadow-md"
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
      data-calendar-transient-control
      data-side={position?.side}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}
